/// <reference types="google.maps" />
import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ENVIRONMENT, GeolocationInfo } from '@athena/util';

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  private ngZone = inject(NgZone);
  private environment = inject(ENVIRONMENT);

  private mapsApiLoaded = false;
  private readonly SCRIPT_ID = 'google-maps-script';
  private readonly API_KEY = this.environment.googleMapsApiKey;

  loadMapsApi(): Promise<void> {
    if (this.mapsApiLoaded || window.google?.maps) {
      return Promise.resolve();
    }

    const existingScript = document.getElementById(this.SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      return new Promise((resolve, reject) => {
        existingScript.addEventListener(
          'load',
          () => {
            this.mapsApiLoaded = true;
            resolve();
          },
          { once: true },
        );

        existingScript.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = this.SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.mapsApiLoaded = true;
        resolve();
      };
      script.onerror = reject;

      document.head.appendChild(script);
    });
  }

  // Geocode address/postcode to lat/lng
  geocodeAddress(address: string, restrictions?: google.maps.GeocoderComponentRestrictions): Observable<GeolocationInfo | null> {
    return from(this.loadMapsApi()).pipe(
      switchMap(() => {
        const geocoder = new google.maps.Geocoder();
        return new Observable<GeolocationInfo>((subscriber) => {
          geocoder.geocode({ address, componentRestrictions: restrictions }, (results, status) => {
            this.ngZone.run(() => {
              if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
                const location = results[0].geometry.location;
                subscriber.next({
                  latitude: location.lat(),
                  longitude: location.lng(),
                });
              } else {
                subscriber.error(status);
              }
              subscriber.complete();
            });
          });
        });
      }),
      catchError(() => of(null)),
    );
  }

  // Reverse geocode lat/lng to address
  reverseGeocode(location: GeolocationInfo): Observable<GeolocationInfo> {
    if (location.latitude === null || location.longitude === null) {
      return of(location);
    }
    const latitude = location.latitude;
    const longitude = location.longitude;
    return from(this.loadMapsApi()).pipe(
      switchMap(() => {
        const geocoder = new google.maps.Geocoder();
        return new Observable<GeolocationInfo>((subscriber) => {
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            this.ngZone.run(() => {
              if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
                const town = results[0].address_components.find((c) => c.types.includes('postal_town'))?.long_name;
                const postcode = results[0].address_components.find((c) => c.types.includes('postal_code'))?.long_name?.split(' ')[0];
                const address = results[0].formatted_address;
                subscriber.next({
                  ...location,
                  info: { town, postcode, address },
                });
              } else {
                subscriber.next(location);
              }
              subscriber.complete();
            });
          });
        });
      }),
    );
  }
}
